export const printHtmlReportToPdf = async (
  htmlContent: Blob,
): Promise<void> => {
  const responseText = await htmlContent.text();

  // Parse JSON response to extract HTML content
  let htmlString: string;
  try {
    const jsonResponse = JSON.parse(responseText);
    htmlString = jsonResponse.htmlContent;

    if (!htmlString) {
      throw new Error("htmlContent property not found in JSON response");
    }
  } catch (parseError) {
    // If parsing fails (invalid JSON), fall back to assuming it's raw HTML
    if (parseError instanceof SyntaxError) {
      console.warn("Response is not valid JSON, treating as raw HTML");
      htmlString = responseText;
    } else {
      // Re-throw non-parsing errors (e.g., htmlContent property missing)
      throw parseError;
    }
  }

  if (!htmlString || htmlString.trim().length === 0) {
    throw new Error("HTML content is empty or contains only whitespace");
  }

  // Inject a self-contained auto-print script into the HTML so the *new tab*
  // drives its own print dialog.  Calling window.print() from the opener would
  // block the parent application until the dialog is dismissed; delegating to
  // the child tab's own load event avoids that entirely.
  const scriptBody =
    "window.addEventListener('load',function(){" +
    "window.print();" +
    "window.addEventListener('afterprint',function(){window.close();});" +
    "});";
  const autoPrintScript = `<script>${scriptBody}</` + `script>`;

  const printReadyHtml = /<\/body>/i.test(htmlString)
    ? htmlString.replace(/<\/body>/i, `${autoPrintScript}</body>`)
    : `${htmlString}${autoPrintScript}`;

  // Load the HTML via a Blob URL so document.writeln is not needed.
  const htmlBlob = new Blob([printReadyHtml], { type: "text/html" });
  const blobUrl = URL.createObjectURL(htmlBlob);

  // ⚠️  IMPORTANT: "noopener,noreferrer" forces the browser to open the new tab
  // in a *separate OS process* (site isolation / process-per-site-instance).
  // Without this flag, Chrome keeps both tabs in the same renderer process and
  // any print dialog in the child tab blocks the *entire process* — freezing
  // scrolling, input, and even page refresh in the parent tab until the dialog
  // is dismissed.
  //
  // Trade-off: the HTML spec requires window.open() to return null when
  // noopener is used, so we can no longer detect a blocked pop-up via the
  // return value.  If the browser blocks the pop-up the user will simply see
  // no new tab; we inform them with a console warning.
  window.open(blobUrl, "_blank", "noopener,noreferrer");

  // Revoke the object URL after enough time for the tab to finish loading and
  // trigger its embedded print dialog.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};
